/**
 * Which products this tenant has bought, for the two places the admin
 * needs the answer: the sidebar (hide a section) and the product routes
 * (redirect rather than render a page every request of which will 403).
 *
 * Deliberately NOT a product module. Sessions and Vault are sold
 * independently and their code is kept apart — neither imports the other
 * — so the thing that knows about BOTH has to sit outside both, next to
 * `nav.ts` which is the other module in that position. It is also why
 * this does not reuse `session-lookups.ts`, which already fetches the
 * same tree: that module belongs to the Sessions product, and the vault
 * pages may not import it. The cost of the separation is one extra
 * `/hierarchy` call per five minutes, cached below; the cost of breaking
 * it is a vault-only tenant pulling in the sessions bundle.
 *
 * None of this is a security control. The API enforces entitlement on
 * every `/vault/**` and `/sessions**` call and refuses with the same 403
 * a non-member gets (see `routes/vault-auth-root.ts`), so everything here
 * is the UI agreeing with the server. Never let an entitlement decision
 * live only in this file.
 */

import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import type { TenantProduct } from '@stoaria-control/schema'
import { hasTenantProduct } from '@stoaria-control/schema'
import { fetchHierarchy } from './hierarchy-api.ts'

/**
 * Its own key, separate from `session-lookups`' — see the module comment
 * for why the two are not merged. Both hit `/hierarchy`; both are cached
 * long enough that neither lands in the sessions page's 5-second poll.
 */
export const TENANT_PRODUCTS_QUERY_KEY: readonly string[] = ['tenant-products']

/**
 * How long an entitlement answer is reused.
 *
 * Same five minutes as the session lookups, for the same reason: long
 * enough to stay out of the poll, short enough that flipping a product on
 * for a tenant shows up without them being told to reload. Billing
 * writing `org_nodes.products` is rare enough that a five-minute lag is
 * not worth a cache invalidation protocol.
 */
export const TENANT_PRODUCTS_STALE_MS = 5 * 60_000

/**
 * The union of every visible tenant's products.
 *
 * `GET /hierarchy` returns one root per tenant the operator is a member
 * of, and `products` is a root-only field (a child node has no
 * entitlement of its own — see `hierarchy-api.ts`). That premise is the
 * whole correctness of the union, and it was FALSE when this was written:
 * the endpoint listed every root in the database, so the union ran over
 * every tenant that exists and one tenant anywhere buying Sessions un-hid
 * Sessions for all of them. The endpoint is scoped by `memberships` now;
 * if this ever stops filtering, check that first. A union is the right
 * answer for the CHROME: a sidebar is one per window, not one per tenant,
 * and hiding Vault from an operator who is in two tenants because the
 * first of them has not bought it would be a lie. The per-tenant decision
 * is the server's, on each request, against the root the request names.
 */
export async function fetchTenantProducts(): Promise<TenantProduct[]> {
  const tree = await fetchHierarchy()
  const products = new Set<TenantProduct>()
  for (const root of tree.roots)
    for (const product of root.products) products.add(product)
  return [...products]
}

/**
 * Fetch-or-reuse against the router's query client.
 *
 * Deliberately does NOT swallow a failure, unlike `fetchSessionLookups`.
 * A missing name renders as a uuid and nobody is locked out; a wrongly
 * empty product list would hide a section the tenant pays for, and
 * caching that for five minutes turns a blip into "the Vault pages
 * disappeared". Callers decide what an unanswerable question means —
 * `loadNavProducts` treats it as unknown, the route guards let it reach
 * the error boundary.
 */
export async function ensureTenantProducts(
  queryClient: QueryClient,
): Promise<TenantProduct[]> {
  return queryClient.ensureQueryData({
    queryKey: TENANT_PRODUCTS_QUERY_KEY,
    queryFn: fetchTenantProducts,
    staleTime: TENANT_PRODUCTS_STALE_MS,
  })
}

/**
 * The products the chrome should filter the nav by, or `null` for "could
 * not find out".
 *
 * The shell renders on every page including `/login`, where the fetch
 * 401s and there is no tenant yet, so this one cannot throw. `null` is
 * kept distinct from `[]`: an empty list is a real tenant that has bought
 * nothing and correctly gets a platform-only sidebar, whereas unknown
 * falls back to the full nav (`navForProducts(null)`). Failing open is
 * the right way round here precisely because the nav is cosmetic — a
 * section that should not be there costs the operator one 403, while a
 * section wrongly hidden after a network blip looks like their account
 * lost a product.
 */
export async function loadNavProducts(
  queryClient: QueryClient,
): Promise<TenantProduct[] | null> {
  try {
    return await ensureTenantProducts(queryClient)
  } catch {
    // Unknown, not empty. See above.
    return null
  }
}

/**
 * Route guard: leave a product's pages before rendering them if the
 * tenant has not bought the product.
 *
 * Redirects to the landing page, which is the one route that always
 * renders for anyone (`routes/index.tsx` swallows its own 401). The
 * alternative — rendering the page and letting every request on it 403 —
 * is what this exists to stop: a sidebar with no Sessions link and a
 * `/sessions` URL in somebody's bookmarks that answers with a wall of
 * permission errors.
 *
 * A FETCH failure is not an entitlement answer, and this fails OPEN on
 * it — same rule as the sidebar, for the same reason. Only a tenant that
 * demonstrably lacks the product is moved; a `/hierarchy` blip leaves an
 * entitled operator on the page they asked for, where the API will tell
 * them the truth. Erring the other way would take the Sessions page down
 * for everyone whenever the tree call was unwell, and this page's
 * hierarchy fetch has always been best-effort (`session-lookups.ts`).
 *
 * A 401 is therefore "unknown" here too, and the /login bounce stays
 * where it already was: on the page's own gated fetch, under
 * `withSessionRedirect`. Two routes racing to redirect is how you get a
 * loop.
 */
export async function requireTenantProduct(
  queryClient: QueryClient,
  product: TenantProduct,
): Promise<void> {
  const products = await loadNavProducts(queryClient)
  if (products === null || hasTenantProduct(products, product)) return
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's documented redirect pattern throws a Redirect object
  throw redirect({ to: '/' })
}
